import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  IonContent,
  IonPage,
  IonText,
  IonSpinner,
  IonButton,
  IonBadge,
} from "@ionic/react";
import moment from "moment";
import Header from "src/components/Header";
import OperariosRemplazos from "src/services/operarioRemplazo";

const BORDO = "#910434"; // color primario de la app (--ion-color-primary)

const formatDate = (value?: string) =>
  value ? moment(value).format("DD/MM/YYYY") : "-";

// Color del badge segun el tipo de novedad del calendario del Contact Center.
const TIPOS: Record<string, string> = {
  REEMPLAZO: "primary",
  REFUERZO: "tertiary",
  DIA_LIBRE: "medium",
  VACACIONES: "success",
  PERMISO: "warning",
};

const tipoColor = (tipo?: string) =>
  TIPOS[String(tipo || "").toUpperCase()] || "medium";

const TAMANO_PAGINA = 20; // registros por página del listado

// Opciones del filtro por tipo (valores tal como vienen del calendario CC).
const OPCIONES_TIPO: { valor: string; label: string }[] = [
  { valor: "", label: "Todos" },
  { valor: "REEMPLAZO", label: "Reemplazo" },
  { valor: "REFUERZO", label: "Refuerzo" },
  { valor: "DIA_LIBRE", label: "Día libre" },
  { valor: "VACACIONES", label: "Vacaciones" },
  { valor: "PERMISO", label: "Permiso" },
];

interface PuntoServicio {
  ps_codigo: string;
  ps_nombre?: string;
}

const etiquetaPs = (p: PuntoServicio) =>
  p.ps_nombre ? `${p.ps_nombre} (${p.ps_codigo})` : p.ps_codigo;

const inputFechaStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 8px",
  borderRadius: 8,
  border: `1px solid ${BORDO}`,
  color: BORDO,
  accentColor: BORDO,
  fontWeight: 600,
  background: "#fff",
  cursor: "pointer",
};

const inputTextoStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 8px",
  borderRadius: 8,
  border: "1px solid #ddd",
  background: "#fff",
  // Color explícito: en el WebView el input hereda el color del tema y el
  // texto tecleado puede quedar invisible sobre el fondo blanco.
  color: "#222",
  WebkitTextFillColor: "#222",
  caretColor: "#222",
};

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  color: "#777",
  marginBottom: 4,
};

const ReporteReemplazosContact: React.FC = () => {
  const [desde, setDesde] = useState(
    moment().startOf("month").format("YYYY-MM-DD")
  );
  const [hasta, setHasta] = useState(
    moment().endOf("month").format("YYYY-MM-DD")
  );
  const [puntos, setPuntos] = useState<PuntoServicio[]>([]);
  const [objeto, setObjeto] = useState(""); // codigo (objeto) del PS seleccionado; vacio = todos
  const [psQuery, setPsQuery] = useState(""); // texto del buscador de PS
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
  const [filtroReemplazante, setFiltroReemplazante] = useState("");
  const [filtroReemplazada, setFiltroReemplazada] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [pagina, setPagina] = useState(1);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const cargarPuntos = async () => {
      try {
        const resp: any = await OperariosRemplazos.reporteContactPuntos();
        setPuntos(resp?.data?.puntos || []);
      } catch (err) {
        console.error("Error al cargar puntos de servicio:", err);
        setPuntos([]);
      }
    };
    cargarPuntos();
  }, []);

  // Sugerencias del buscador de PS: matchea por nombre o codigo.
  const sugerencias = useMemo(() => {
    const q = psQuery.trim().toLowerCase();
    if (!q) return [];
    return puntos
      .filter(
        (p) =>
          String(p.ps_nombre || "").toLowerCase().includes(q) ||
          String(p.ps_codigo || "").toLowerCase().includes(q)
      )
      .slice(0, 50);
  }, [puntos, psQuery]);

  const seleccionarPs = (p: PuntoServicio) => {
    setObjeto(p.ps_codigo);
    setPsQuery(etiquetaPs(p));
    setMostrarSugerencias(false);
  };

  const limpiarPs = () => {
    setObjeto("");
    setPsQuery("");
    setMostrarSugerencias(false);
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp: any = await OperariosRemplazos.reporteContactListar({
        desde,
        hasta,
        objeto: objeto || undefined,
      });
      setItems(resp?.data?.items || []);
    } catch (err: any) {
      console.error("Error al cargar reporte de remplazos Contact:", err);
      setError(
        err?.response?.data?.error || "No se pudo cargar el reporte."
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [desde, hasta, objeto]);

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtros en vivo por reemplazante/reemplazada/tipo sobre el resultado cargado.
  const itemsFiltrados = useMemo(() => {
    const fRte = filtroReemplazante.trim().toLowerCase();
    const fRda = filtroReemplazada.trim().toLowerCase();
    const fTipo = filtroTipo.trim().toUpperCase();
    if (!fRte && !fRda && !fTipo) return items;
    return items.filter((r: any) => {
      const okRte =
        !fRte || String(r.reemplazante || "").toLowerCase().includes(fRte);
      const okRda =
        !fRda || String(r.reemplazada || "").toLowerCase().includes(fRda);
      const okTipo = !fTipo || String(r.tipo || "").toUpperCase() === fTipo;
      return okRte && okRda && okTipo;
    });
  }, [items, filtroReemplazante, filtroReemplazada, filtroTipo]);

  // Volver a la primera página cuando cambian los datos o los filtros en vivo.
  useEffect(() => {
    setPagina(1);
  }, [items, filtroReemplazante, filtroReemplazada, filtroTipo]);

  const totalPaginas = Math.max(1, Math.ceil(itemsFiltrados.length / TAMANO_PAGINA));
  const paginaActual = Math.min(pagina, totalPaginas);
  const itemsPagina = useMemo(
    () =>
      itemsFiltrados.slice(
        (paginaActual - 1) * TAMANO_PAGINA,
        paginaActual * TAMANO_PAGINA
      ),
    [itemsFiltrados, paginaActual]
  );

  return (
    <IonPage>
      <Header titulo="Reporte de Remplazos Contact" />
      <IonContent>
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Filtros */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: 12,
              boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
              padding: 16,
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "flex-end",
            }}
          >
            {/* Buscador de Punto de Servicio */}
            <div style={{ flex: "1 1 100%", minWidth: 200, position: "relative" }}>
              <div style={labelStyle}>Punto de Servicio</div>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  placeholder="Buscar por nombre o código (vacío = todos)"
                  value={psQuery}
                  onChange={(e) => {
                    setPsQuery(e.target.value);
                    setObjeto(""); // al editar el texto se pierde la selección previa
                    setMostrarSugerencias(true);
                  }}
                  onFocus={() => setMostrarSugerencias(true)}
                  onBlur={() => setTimeout(() => setMostrarSugerencias(false), 150)}
                  style={{
                    ...inputTextoStyle,
                    paddingRight: 34,
                    border: objeto ? `1px solid ${BORDO}` : inputTextoStyle.border,
                  }}
                />
                {psQuery ? (
                  <button
                    type="button"
                    onClick={limpiarPs}
                    aria-label="Limpiar punto de servicio"
                    style={{
                      position: "absolute",
                      right: 6,
                      top: "50%",
                      transform: "translateY(-50%)",
                      border: "none",
                      background: "transparent",
                      color: "#777",
                      fontSize: 18,
                      cursor: "pointer",
                      padding: 4,
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                ) : null}
              </div>
              {mostrarSugerencias && psQuery.trim() && !objeto ? (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 20,
                    background: "#fff",
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    marginTop: 4,
                    maxHeight: 220,
                    overflowY: "auto",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  }}
                >
                  {sugerencias.length === 0 ? (
                    <div style={{ padding: "10px 12px", fontSize: 14, color: "#777" }}>
                      Sin coincidencias.
                    </div>
                  ) : (
                    sugerencias.map((p) => (
                      <div
                        key={p.ps_codigo}
                        onMouseDown={() => seleccionarPs(p)}
                        style={{
                          padding: "10px 12px",
                          cursor: "pointer",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <div style={{ fontSize: 14, fontWeight: 600, color: "#222" }}>
                          {p.ps_nombre || p.ps_codigo}
                        </div>
                        <div style={{ fontSize: 12, color: "#777" }}>{p.ps_codigo}</div>
                      </div>
                    ))
                  )}
                </div>
              ) : null}
            </div>

            <div style={{ flex: 1, minWidth: 130 }}>
              <div style={labelStyle}>Desde</div>
              <input
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                style={inputFechaStyle}
              />
            </div>
            <div style={{ flex: 1, minWidth: 130 }}>
              <div style={labelStyle}>Hasta</div>
              <input
                type="date"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                style={inputFechaStyle}
              />
            </div>
            {/* Filtros en vivo por persona */}
            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={labelStyle}>Reemplazante</div>
              <input
                type="text"
                placeholder="Filtrar por reemplazante"
                value={filtroReemplazante}
                onChange={(e) => setFiltroReemplazante(e.target.value)}
                style={inputTextoStyle}
              />
            </div>
            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={labelStyle}>Reemplazada</div>
              <input
                type="text"
                placeholder="Filtrar por reemplazada"
                value={filtroReemplazada}
                onChange={(e) => setFiltroReemplazada(e.target.value)}
                style={inputTextoStyle}
              />
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={labelStyle}>Tipo</div>
              <select
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value)}
                style={inputTextoStyle}
              >
                {OPCIONES_TIPO.map((op) => (
                  <option key={op.valor || "todos"} value={op.valor}>
                    {op.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Botón al pie, debajo de todos los filtros */}
            <div style={{ flexBasis: "100%" }}>
              <IonButton
                expand="block"
                onClick={cargar}
                disabled={loading}
                style={{ height: 44, fontWeight: 600 }}
              >
                Filtrar
              </IonButton>
            </div>
          </div>

          {/* Total de registros */}
          {!loading && !error && itemsFiltrados.length > 0 ? (
            <div>
              <IonBadge color="primary" style={{ fontSize: 13, padding: "6px 10px" }}>
                Registros: {itemsFiltrados.length}
                {itemsFiltrados.length !== items.length ? ` de ${items.length}` : ""}
              </IonBadge>
            </div>
          ) : null}

          {/* Lista / estados */}
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 30 }}>
              <IonSpinner />
            </div>
          ) : error ? (
            <div style={{ textAlign: "center", marginTop: 20 }}>
              <IonText color="danger">{error}</IonText>
            </div>
          ) : itemsFiltrados.length === 0 ? (
            <div style={{ textAlign: "center", marginTop: 20 }}>
              <IonText color="medium">
                {items.length > 0
                  ? "Sin resultados con los filtros aplicados."
                  : "Sin reemplazos en el rango seleccionado."}
              </IonText>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {itemsPagina.map((r: any, idx: number) => (
                <div
                  key={`rrc-${idx}`}
                  style={{
                    background: "#ffffff",
                    borderRadius: 12,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                    padding: 16,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 10,
                      marginBottom: 8,
                    }}
                  >
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: BORDO }}>
                      {r.contrato || "-"}
                    </h2>
                    <IonBadge color={tipoColor(r.tipo)} style={{ fontSize: 13, padding: "6px 10px" }}>
                      {r.tipo || "-"}
                    </IonBadge>
                  </div>
                  <div style={{ fontSize: 15, color: "#222", lineHeight: 1.5 }}>
                    <div>
                      <span style={{ color: "#777" }}>Fecha: </span>
                      {formatDate(r.fecha)}
                    </div>
                    <div>
                      <span style={{ color: "#777" }}>Reemplazante (cubre Contact): </span>
                      {r.reemplazante || "-"}
                    </div>
                    <div>
                      <span style={{ color: "#777" }}>Reemplazada (a quien se le cubre): </span>
                      {r.reemplazada || "-"}
                    </div>
                    <div>
                      <span style={{ color: "#777" }}>Horario: </span>
                      {r.hora_inicio || "-"} a {r.hora_fin || "-"}
                    </div>
                    <div>
                      <span style={{ color: "#777" }}>Fiscal: </span>
                      {r.fiscal || "-"}
                    </div>
                  </div>
                </div>
              ))}

              {/* Paginador */}
              {totalPaginas > 1 ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    marginTop: 4,
                  }}
                >
                  <IonButton
                    size="small"
                    fill="outline"
                    disabled={paginaActual <= 1}
                    onClick={() => setPagina(paginaActual - 1)}
                  >
                    Anterior
                  </IonButton>
                  <span style={{ fontSize: 14, color: "#555" }}>
                    Página {paginaActual} de {totalPaginas}
                  </span>
                  <IonButton
                    size="small"
                    fill="outline"
                    disabled={paginaActual >= totalPaginas}
                    onClick={() => setPagina(paginaActual + 1)}
                  >
                    Siguiente
                  </IonButton>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ReporteReemplazosContact;
